import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   WriteNFC.js — reduced friction redesign
   UX principles:
   1. Single flow — no confusing mode tabs. Auto-detect what to write based on
      what the user selects (profile vs URL vs custom)
   2. Big clear CTA — one "Write to card" button always visible
   3. Status is obvious — animated ring shows scan readiness, green = success
   4. Permission errors explained clearly with one-tap retry
   5. "Link to your account" shown as inline step after success, not hidden
   6. Works on both desktop (simulated/manual) and NFC-capable mobile
───────────────────────────────────────────────────────────────────────────── */

const B = '#1c73ba';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
:root{--blue:#1c73ba;--bl:#e8f3fb;--bd:#155fa0;--text:#111827;--muted:#6b7280;--border:#e5e7eb;--bg:#f3f4f6;--red:#ef4444;--green:#10b981;--gold:#f59e0b;--purple:#7c3aed}
*{box-sizing:border-box;margin:0;padding:0}
.nfc-root{min-height:100dvh;background:var(--bg);font-family:'Inter',system-ui,sans-serif;color:var(--text);-webkit-font-smoothing:antialiased}
.nfc-nav{position:sticky;top:0;z-index:50;background:#fff;border-bottom:1px solid var(--border);padding:0 20px;height:56px;display:flex;align-items:center;gap:10px}
.nfc-logo{font-size:1.1rem;font-weight:700;color:var(--blue);text-decoration:none;margin-right:auto}
.nfc-logo span{color:var(--gold)}
.ic-btn{width:34px;height:34px;border:1px solid var(--border);border-radius:8px;background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--muted);transition:all .15s;flex-shrink:0}
.ic-btn:hover{border-color:var(--blue);color:var(--blue);background:var(--bl)}
.nfc-body{max-width:520px;margin:0 auto;padding:24px 18px 60px}
/* Write target picker */
.nfc-pick-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px}
.nfc-pick{padding:16px 14px;border:1.5px solid var(--border);border-radius:12px;background:#fff;cursor:pointer;text-align:left;font-family:inherit;transition:all .15s;position:relative}
.nfc-pick:hover{border-color:var(--blue);background:var(--bl)}
.nfc-pick.sel{border-color:var(--blue);background:var(--bl)}
.nfc-pick-check{position:absolute;top:10px;right:10px;width:18px;height:18px;border-radius:50%;background:var(--blue);display:flex;align-items:center;justify-content:center}
.nfc-pick-icon{font-size:24px;margin-bottom:8px;display:block}
.nfc-pick-title{font-size:13px;font-weight:700;color:var(--text);margin-bottom:2px}
.nfc-pick-sub{font-size:11px;color:var(--muted);line-height:1.4}
.nfc-card{background:#fff;border:1px solid var(--border);border-radius:12px;padding:18px 20px;margin-bottom:14px}
.nfc-card-title{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:var(--blue);margin-bottom:12px}
.nfc-label{display:block;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.8px;margin-bottom:5px}
.nfc-input{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:8px;font-size:13px;color:var(--text);outline:none;font-family:inherit;transition:border .15s;background:#fff}
.nfc-input:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(28,115,186,0.1)}
.nfc-select{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:8px;font-size:13px;color:var(--text);outline:none;font-family:inherit;background:#fff;cursor:pointer;transition:border .15s}
.nfc-select:focus{border-color:var(--blue)}
/* NFC ring animation */
.nfc-ring-wrap{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px 0 24px;gap:16px}
.nfc-ring{width:100px;height:100px;border-radius:50%;border:3px solid var(--border);display:flex;align-items:center;justify-content:center;position:relative;transition:all .3s}
.nfc-ring.ready{border-color:rgba(28,115,186,0.3)}
.nfc-ring.ready::before{content:'';position:absolute;inset:-8px;border-radius:50%;border:2px solid rgba(28,115,186,0.2);animation:ringPulse 2s ease-in-out infinite}
.nfc-ring.ready::after{content:'';position:absolute;inset:-16px;border-radius:50%;border:1.5px solid rgba(28,115,186,0.1);animation:ringPulse 2s ease-in-out infinite .3s}
.nfc-ring.writing{border-color:var(--blue);animation:ringGlow 1.2s ease-in-out infinite}
.nfc-ring.success{border-color:var(--green);background:rgba(16,185,129,0.07)}
.nfc-ring.error{border-color:var(--red);background:rgba(239,68,68,0.07)}
@keyframes ringPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.4;transform:scale(1.05)}}
@keyframes ringGlow{0%,100%{box-shadow:0 0 0 0 rgba(28,115,186,0.4)}50%{box-shadow:0 0 0 16px rgba(28,115,186,0)}}
.nfc-ring-label{font-size:14px;font-weight:600;color:var(--text);text-align:center}
.nfc-ring-sub{font-size:12px;color:var(--muted);text-align:center;max-width:280px;line-height:1.6}
/* Write CTA */
.nfc-write-btn{width:100%;padding:14px;border-radius:12px;border:none;background:var(--blue);color:#fff;font-size:15px;font-weight:700;cursor:pointer;font-family:inherit;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:9px;letter-spacing:-.2px}
.nfc-write-btn:hover:not(:disabled){background:var(--bd);transform:translateY(-1px)}
.nfc-write-btn:disabled{background:#9ca3af;cursor:not-allowed}
/* URL preview */
.nfc-url-preview{padding:10px 13px;background:var(--bg);border:1px solid var(--border);border-radius:8px;font-size:12px;font-family:monospace;color:var(--blue);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;word-break:break-all;margin-top:10px}
/* History log */
.nfc-log{display:flex;flex-direction:column;gap:8px}
.nfc-log-item{display:flex;align-items:center;gap:10px;padding:10px 12px;background:#fff;border:1px solid var(--border);border-radius:9px;font-size:13px}
.nfc-log-status{width:24px;height:24px;border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.nfc-log-text{flex:1;min-width:0}
.nfc-log-url{font-size:11px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nfc-log-time{font-size:11px;color:var(--muted);flex-shrink:0}
/* Post-write actions */
.nfc-post{background:#fff;border:1px solid var(--border);border-radius:12px;overflow:hidden;margin-top:14px}
.nfc-post-head{padding:14px 18px;background:rgba(16,185,129,0.06);border-bottom:1px solid rgba(16,185,129,0.15);display:flex;align-items:center;gap:10px}
.nfc-post-body{padding:14px 18px}
.nfc-post-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px}
.nfc-action-btn{padding:8px 16px;border-radius:8px;border:none;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;transition:all .15s}
/* Info boxes */
.nfc-info{padding:12px 14px;border-radius:9px;font-size:13px;display:flex;gap:10px;align-items:flex-start;line-height:1.6}
.nfc-info.blue{background:var(--bl);border:1px solid rgba(28,115,186,0.2);color:var(--bd)}
.nfc-info.amber{background:#fffbeb;border:1px solid rgba(245,158,11,0.25);color:#92400e}
.nfc-info.green{background:#ecfdf5;border:1px solid rgba(16,185,129,0.25);color:#065f46}
.nfc-info.red{background:#fef2f2;border:1px solid rgba(239,68,68,0.2);color:#b91c1c}
.nfc-spinner{width:18px;height:18px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite;display:inline-block;flex-shrink:0}
.nfc-spinner.dark{border:2px solid var(--bl);border-top-color:var(--blue)}
@keyframes spin{to{transform:rotate(360deg)}}
.nfc-compatible-list{display:flex;flex-direction:column;gap:6px}
.nfc-compat-row{display:flex;align-items:center;gap:8px;font-size:12px;padding:8px 12px;border-radius:8px;background:#f9fafb;border:1px solid var(--border)}
.nfc-compat-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:99px}
`;

// Icons
const Ic = {
  back:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  nfc:   <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 7c-1.333 2-2 4-2 5s.667 3 2 5"/><path d="M10 9c-.667 1.333-1 2.667-1 4s.333 2.667 1 4"/><path d="M14 11c-.333.667-.5 1.333-.5 2s.167 1.333.5 2"/><path d="M18 7c1.333 2 2 4 2 5s-.667 3-2 5"/></svg>,
  write: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  retry: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  link:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  copy:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  ext:   <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  info:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  tag:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  card:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  globe: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  key:   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>,
};

// Write modes
const MODES = [
  { id:'profile', icon:'🪪', title:'My profile',   sub:'Link to your Flap profile page' },
  { id:'url',     icon:'🔗', title:'Custom URL',   sub:'Any website or link' },
  { id:'flapid',  icon:'🏷️', title:'Any FlapID',  sub:'Write someone else\'s Flap ID' },
];

const NFC_SUPPORTED = 'NDEFReader' in window;

export default function WriteNFC() {
  const navigate = useNavigate();
  const [user,    setUser]    = useState(null);
  const [mode,    setMode]    = useState('profile');
  const [urlIn,   setUrlIn]   = useState('https://');
  const [flapIn,  setFlapIn]  = useState('');
  const [accIdx,  setAccIdx]  = useState(0);
  const [phase,   setPhase]   = useState('idle'); // idle | ready | writing | success | error | no-nfc
  const [errMsg,  setErrMsg]  = useState('');
  const [lastUrl, setLastUrl] = useState('');
  const [log,     setLog]     = useState([]);
  const [copied,  setCopied]  = useState(false);
  const [linking, setLinking] = useState(false);
  const [linked,  setLinked]  = useState(false);
  const readerRef = useRef(null);

  useEffect(() => {
    api.get('/users/profile').then(({ data }) => setUser(data)).catch(() => {});
    if (!NFC_SUPPORTED) setPhase('no-nfc');
  }, []);

  // Resolve the URL to write
  const resolveUrl = () => {
    const origin = window.location.origin;
    if (mode === 'profile') {
      const acc = user?.accessories?.[accIdx];
      const flapId = acc?.flapid || user?.primaryFlapid || user?.username || '';
      return flapId ? `${origin}/${flapId}` : '';
    }
    if (mode === 'url')    return urlIn.trim();
    if (mode === 'flapid') return flapIn.trim() ? `${origin}/${flapIn.trim()}` : '';
    return '';
  };

  const targetUrl = resolveUrl();
  const canWrite  = !!targetUrl && targetUrl !== 'https://';

  const write = async () => {
    if (!canWrite) return;
    if (!NFC_SUPPORTED) { setPhase('no-nfc'); return; }
    setPhase('ready');
    setErrMsg('');

    try {
      const ndef = new window.NDEFReader();
      readerRef.current = ndef;
      setPhase('writing');

      await ndef.write({ records: [{ recordType: 'url', data: targetUrl }] });

      setLastUrl(targetUrl);
      setPhase('success');
      setLog(prev => [{
        url: targetUrl,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ok: true,
      }, ...prev.slice(0, 9)]);
      setLinked(false);
    } catch (err) {
      const isAbort   = err.name === 'AbortError';
      const isPerm    = err.name === 'NotAllowedError' || err.message?.toLowerCase().includes('permission');
      const isTimeout = err.message?.toLowerCase().includes('timeout') || err.message?.toLowerCase().includes('timed');

      if (isAbort) { setPhase('idle'); return; }

      let msg = 'Write failed.';
      if (isPerm)    msg = 'Permission denied. Tap "Allow" when your browser asks for NFC access, then try again.';
      else if (isTimeout) msg = 'No card detected in time. Hold a blank NFC card firmly against your phone and try again.';
      else msg = err.message || 'Unknown error. Make sure NFC is enabled on your device.';

      setErrMsg(msg);
      setPhase('error');
      setLog(prev => [{ url: targetUrl, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), ok: false }, ...prev.slice(0, 9)]);
    }
  };

  // Link the written card to this account
  const linkToAccount = async () => {
    if (!user || mode !== 'profile') return;
    const acc = user?.accessories?.[accIdx];
    if (!acc?.flapid) return;
    setLinking(true);
    try {
      // The card is already linked — just marking confirmation in UX
      // Backend: tag was already written with the user's flapid URL
      setLinked(true);
    } catch {}
    setLinking(false);
  };

  const copyUrl = () => {
    navigator.clipboard?.writeText(lastUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => { setPhase('idle'); setErrMsg(''); };

  const ringClass = { idle:'', ready:'ready', writing:'writing', success:'success', error:'error', 'no-nfc':'' }[phase] || '';

  const RING_CONTENT = {
    idle:     { icon: Ic.nfc, color: '#9ca3af', label: 'Ready to write', sub: 'Choose what to write below, then tap Write' },
    ready:    { icon: Ic.nfc, color: B,          label: 'Hold card near phone', sub: 'Keep the card steady against the back' },
    writing:  { icon: <span className="nfc-spinner" style={{ width: 28, height: 28, borderWidth: 3, borderColor: 'rgba(28,115,186,.2)', borderTopColor: B }}/>, color: B, label: 'Writing…', sub: 'Do not move the card' },
    success:  { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>, color: '#10b981', label: 'Written successfully!', sub: 'The card is ready to use' },
    error:    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, color: '#ef4444', label: 'Write failed', sub: errMsg },
    'no-nfc': { icon: Ic.nfc, color: '#d1d5db', label: 'NFC not available', sub: 'Your browser or device does not support Web NFC' },
  };

  const ring = RING_CONTENT[phase] || RING_CONTENT.idle;
  const showPost  = phase === 'success';
  const showError = phase === 'error';

  const profileAcc = user?.accessories?.filter(a => a.flapid) || [];

  return (
    <div className="nfc-root">
      <style>{CSS}</style>

      <nav className="nfc-nav">
        <button className="ic-btn" onClick={() => navigate('/profile')}>{Ic.back}</button>
        <a href="/" className="nfc-logo">flap<span>.</span></a>
        {!NFC_SUPPORTED && (
          <div className="nfc-info amber" style={{ padding: '4px 10px', margin: 0, fontSize: 11 }}>
            {Ic.info} Desktop — NFC unavailable
          </div>
        )}
      </nav>

      <div className="nfc-body">
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: 4 }}>Write NFC card</h1>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, lineHeight: 1.6 }}>Choose what to write to a blank NFC tag or card. Takes about 1 second.</p>

        {/* ── Step 1: What to write ─────────────────────────────────── */}
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Step 1 — What to write</div>
        <div className="nfc-pick-grid" style={{ marginBottom: 16 }}>
          {MODES.map(m => (
            <div key={m.id} className={`nfc-pick ${mode === m.id ? 'sel' : ''}`} onClick={() => { setMode(m.id); reset(); }}>
              {mode === m.id && <div className="nfc-pick-check">{Ic.check}</div>}
              <span className="nfc-pick-icon">{m.icon}</span>
              <div className="nfc-pick-title">{m.title}</div>
              <div className="nfc-pick-sub">{m.sub}</div>
            </div>
          ))}
          {/* pad grid to 2 cols — custom text placeholder, could add later */}
          <div className="nfc-pick" style={{ opacity:.45, cursor:'default', border:'1.5px dashed #e5e7eb' }}>
            <span className="nfc-pick-icon">📲</span>
            <div className="nfc-pick-title">vCard contact</div>
            <div className="nfc-pick-sub">Coming soon</div>
          </div>
        </div>

        {/* ── Step 2: Configure ─────────────────────────────────────── */}
        {mode === 'profile' && user && (
          <div className="nfc-card" style={{ marginBottom: 14 }}>
            <div className="nfc-card-title">Your profile</div>
            {profileAcc.length > 1 && (
              <div style={{ marginBottom: 10 }}>
                <label className="nfc-label">Select accessory</label>
                <select className="nfc-select" value={accIdx} onChange={e => { setAccIdx(+e.target.value); reset(); }}>
                  {profileAcc.map((a, i) => (
                    <option key={i} value={i}>{a.type} — /{a.flapid}</option>
                  ))}
                  <option value={-1}>Primary profile</option>
                </select>
              </div>
            )}
            {targetUrl && <div className="nfc-url-preview">→ {targetUrl}</div>}
            {!targetUrl && (
              <div className="nfc-info amber" style={{ marginTop: 8 }}>
                {Ic.info} No FlapID found. <button onClick={() => navigate('/profile?tab=accessories')} style={{ border:'none',background:'none',color:'#1c73ba',cursor:'pointer',fontWeight:600,fontSize:13,fontFamily:'inherit',padding:0 }}>Add one in Profile →</button>
              </div>
            )}
          </div>
        )}

        {mode === 'url' && (
          <div className="nfc-card" style={{ marginBottom: 14 }}>
            <div className="nfc-card-title">URL to write</div>
            <label className="nfc-label">Web address</label>
            <input className="nfc-input" value={urlIn} onChange={e => { setUrlIn(e.target.value); reset(); }}
              placeholder="https://yoursite.com"
              onFocus={e => { if (e.target.value === 'https://') e.target.select(); }}/>
            {targetUrl && targetUrl !== 'https://' && <div className="nfc-url-preview">→ {targetUrl}</div>}
          </div>
        )}

        {mode === 'flapid' && (
          <div className="nfc-card" style={{ marginBottom: 14 }}>
            <div className="nfc-card-title">FlapID to write</div>
            <label className="nfc-label">FlapID or username</label>
            <input className="nfc-input" value={flapIn} onChange={e => { setFlapIn(e.target.value); reset(); }}
              placeholder="e.g. bikalpa-dhungana"/>
            {targetUrl && <div className="nfc-url-preview">→ {targetUrl}</div>}
          </div>
        )}

        {/* ── NFC ring status ───────────────────────────────────────── */}
        {NFC_SUPPORTED ? (
          <div className="nfc-ring-wrap">
            <div className={`nfc-ring ${ringClass}`} style={{ borderColor: phase === 'idle' ? '#e5e7eb' : undefined }}>
              <div style={{ color: ring.color }}>{ring.icon}</div>
            </div>
            <div>
              <div className="nfc-ring-label" style={{ color: ring.color === '#9ca3af' ? '#374151' : ring.color }}>{ring.label}</div>
              <div className="nfc-ring-sub">{ring.sub}</div>
            </div>
          </div>
        ) : (
          <div className="nfc-card" style={{ textAlign: 'center', padding: '24px 20px', marginBottom: 14 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>💻</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>NFC not available on this device</div>
            <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7, marginBottom: 16 }}>
              Web NFC requires an Android phone with Chrome. Copy the link below and use a dedicated NFC writing app instead.
            </div>
            {targetUrl && (
              <>
                <div className="nfc-url-preview">{targetUrl}</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                  <button className="nfc-action-btn" onClick={copyUrl}
                    style={{ background: B, color: '#fff', flex: 1 }}>
                    {copied ? <>{Ic.check} Copied!</> : <>{Ic.copy} Copy link</>}
                  </button>
                  <a href={targetUrl} target="_blank" rel="noreferrer" className="nfc-action-btn"
                    style={{ background: '#f3f4f6', color: '#374151', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px' }}>
                    {Ic.ext} Preview
                  </a>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Write button ──────────────────────────────────────────── */}
        {NFC_SUPPORTED && (phase === 'idle' || phase === 'error') && (
          <button className="nfc-write-btn" disabled={!canWrite} onClick={write}>
            {Ic.write} Write to card
            {!canWrite && <span style={{ fontSize: 11, fontWeight: 400, opacity: .8 }}>— fill in the details above first</span>}
          </button>
        )}

        {NFC_SUPPORTED && phase === 'ready' && (
          <button className="nfc-write-btn" style={{ background: '#f59e0b' }} onClick={() => { readerRef.current?.abort?.(); reset(); }}>
            Cancel
          </button>
        )}

        {NFC_SUPPORTED && phase === 'writing' && (
          <button className="nfc-write-btn" disabled>
            <span className="nfc-spinner"/> Writing…
          </button>
        )}

        {NFC_SUPPORTED && phase === 'success' && (
          <button className="nfc-write-btn" style={{ background: '#10b981' }} onClick={() => { reset(); }}>
            Write another card
          </button>
        )}

        {/* ── Post-write actions ────────────────────────────────────── */}
        {showPost && lastUrl && (
          <div className="nfc-post">
            <div className="nfc-post-head">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>Card written</span>
            </div>
            <div className="nfc-post-body">
              <div className="nfc-url-preview" style={{ marginBottom: 12 }}>{lastUrl}</div>
              <div className="nfc-post-row">
                <span style={{ fontSize: 13, color: '#374151' }}>Copy link</span>
                <button className="nfc-action-btn" onClick={copyUrl} style={{ background: B, color: '#fff' }}>
                  {copied ? 'Copied!' : <>{Ic.copy} Copy</>}
                </button>
              </div>
              <div className="nfc-post-row">
                <span style={{ fontSize: 13, color: '#374151' }}>Test it in browser</span>
                <a href={lastUrl} target="_blank" rel="noreferrer" className="nfc-action-btn" style={{ background: '#f3f4f6', color: '#374151', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {Ic.ext} Open
                </a>
              </div>
              {mode === 'profile' && user && !linked && (
                <div className="nfc-info green" style={{ marginTop: 4 }}>
                  {Ic.check}
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>Card linked to your account</div>
                    <div style={{ fontSize: 12 }}>Your NFC card is already pointing to your profile. Anyone who taps it will see your page.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Error help ────────────────────────────────────────────── */}
        {showError && (
          <div style={{ marginTop: 14 }}>
            <div className="nfc-info red" style={{ marginBottom: 12 }}>
              {Ic.info}
              <div>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>What to check</div>
                <ul style={{ paddingLeft: 14, fontSize: 12, lineHeight: 1.8 }}>
                  <li>NFC is turned on in Android Settings → Connected devices → NFC</li>
                  <li>You're using Chrome on Android (not Safari, Firefox, or iOS)</li>
                  <li>Hold the card flat against the back of your phone — don't move it</li>
                  <li>The card is blank (or has been wiped) — pre-written cards may be locked</li>
                </ul>
              </div>
            </div>
            <button className="nfc-write-btn" onClick={() => { setPhase('idle'); }}>
              {Ic.retry} Try again
            </button>
          </div>
        )}

        {/* ── Write history ─────────────────────────────────────────── */}
        {log.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
              This session — {log.length} write{log.length > 1 ? 's' : ''}
            </div>
            <div className="nfc-log">
              {log.map((l, i) => (
                <div key={i} className="nfc-log-item">
                  <div className="nfc-log-status" style={{ background: l.ok ? '#ecfdf5' : '#fef2f2' }}>
                    {l.ok
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    }
                  </div>
                  <div className="nfc-log-text">
                    <div style={{ fontSize: 12, fontWeight: 600, color: l.ok ? '#065f46' : '#b91c1c' }}>{l.ok ? 'Success' : 'Failed'}</div>
                    <div className="nfc-log-url">{l.url}</div>
                  </div>
                  <div className="nfc-log-time">{l.time}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Device compatibility ──────────────────────────────────── */}
        <div className="nfc-card" style={{ marginTop: 24 }}>
          <div className="nfc-card-title">Device compatibility</div>
          <div className="nfc-compatible-list">
            {[
              { label: 'Android + Chrome 89+', ok: true },
              { label: 'iPhone / iOS (any)', ok: false, note: 'Not supported — use NFC Tools app' },
              { label: 'Desktop browsers',   ok: false, note: 'Not supported — use Android' },
              { label: 'NTAG213 / 215 / 216 cards', ok: true, note: 'Recommended blank tags' },
              { label: 'Mifare Classic',     ok: false, note: 'Limited compatibility' },
            ].map((r, i) => (
              <div key={i} className="nfc-compat-row">
                <div className="nfc-compat-dot" style={{ background: r.ok ? '#10b981' : '#f59e0b' }}/>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>{r.label}</span>
                  {r.note && <span style={{ color: '#6b7280', marginLeft: 6 }}>— {r.note}</span>}
                </div>
                {r.ok
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                }
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}